import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { CellClickedEvent, CellContextMenuEvent, CellEditingStartedEvent, CellEditingStoppedEvent, CellEditorSelectorResult, CellFocusedEvent, CellPosition, CellRendererSelectorResult, CellValueChangedEvent, ColDef, ColGroupDef, ColSpanParams, Column, ColumnState, FilterChangedEvent, FilterModel, GetRowIdFunc, GetRowIdParams, GridOptions, GridReadyEvent, ICellEditorParams, ICellRendererParams, IRowDragItem, IRowNode, RowDragEndEvent, RowDragMoveEvent, RowHeightParams, SortChangedEvent } from "ag-grid-community";
import { Config } from 'src/modules/app-template/models/config.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { ExcelColumn } from 'src/modules/enaio/netgen/ExcelColumn';
import { GuiUtils, GuiUtilsInternal } from 'src/modules/utils/misc/gui-utils';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { TableCellButton } from '../../models/table-cell-button.model';
import { TableCellEvent } from '../../models/table-cell-event.model';
import { TableCell } from '../../models/table-cell.model';
import { TableData } from '../../models/table-data.model';
import { TableRowEvent } from '../../models/table-row-event.model';
import { TableRow } from '../../models/table-row.model';
import { TableSizer } from '../../models/table-sizer.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { TableCellType } from '../../netgen/TableCellType';
import { TableColumn } from '../../netgen/TableColumn';
import { SmTableCellEditorComponent } from '../sm-table-cell-editor/sm-table-cell-editor.component';
import { SmTableCellComponent } from '../sm-table-cell/sm-table-cell.component';
import { SmTableHeaderComponent } from '../sm-table-header/sm-table-header.component';
import { SmTableStatus } from '../../models/sm-table-status.model';
import { SmTableColumnStatus } from '../../models/sm-table-column-status.model';
import { Strings } from 'src/modules/utils/shared/strings';

@Component({
    selector: 'sm-table',
    templateUrl: './sm-table.component.html',
    styleUrl: './sm-table.component.scss',
    standalone: false
})
export class SmTableComponent {

    static defaultUseTableMenu = true;
    static defaultUseColumnMenu = true;
    static defaultActionButtons = false;
    static defaultFilterButtons = true;
    
    static COLUMN_ID_REMOVE = "___???specialDelete???___";
    static COLUMN_ID_EDIT_ROW_FULL_SCREEN = "___???specialEditRowFullScreen???___";

    static locale: any = AG_GRID_LOCALE_DE;

    static setLanguage(lng: string): void {
        if (lng == "de") {
            AG_GRID_LOCALE_DE.noRowsToShow = "Keine Daten vorhanden";
        }
        SmTableComponent.locale = lng == "de" ? AG_GRID_LOCALE_DE : {};
    }

    _SmTable2Component = SmTableComponent;

    app = inject(MainAppService);

    @Input()
    compact: boolean = null;
    @Input()
    tableSizer = true;
    @Input()
    columnWidthsByContent = false;
    @Input()
    horizontalScroll = false;
    @Input()
    allowFilter = true;
    _floatingFilter = false;
    @Input()
    autoHeight = false;
    @Input()
    tabForSameRow = false;
    @Input()
    displayRowNumbers = false;
    @Input()
    displaySideBar = false;
    @Input()
    overrideUseTableCellRenderer: boolean = null;
    _allowAddRow = false;
    _allowRemoveRow = false;
    @Input()
    removeRowWarningMessage: string = null;
    @Input()
    handleRemove = false;
    @Input()
    allowCellTextSelection = true;
    _allowRowDrag = false;
    @Input()
    rowDragManaged = true;
    @Input()
    headerHeight: number = null;
    @Input()
    alowSorting = true;
    @Input()
    addRowOnTabInLastCell = false;
    _fullScreenRowEditor = false;
    _flashChanges = false;
    @Input()
    multiSelection = false;
    @Input()
    editOnClick = true;
    @Input()
    disabled = false;
    _readOnly = false;
    _autoRowHeight = false;
    @Input()
    summaryRow: null | "numRows" | "numSel" | "numRowsNumSel" = null;
    @Input()
    headerAlign: "center" | "left" | "right" | undefined = null;    
    @Input()
    isTree = false;
    @Input()
    invalidCellStyleClass = "xbg-red-100";
    _pinnedBottomRows: TableRow[];
    _checkBoxSelection = false;
    @Input()
    dropDraggedRow: (source: TableRow, dest: TableRow, checkOnly: boolean) => Partial<{ allowDrop: boolean, dropText: string, styleClasses: string[] }>;
    @Input()
    getRowDragText: (params: IRowDragItem, dragItemCount: number) => string;
    @Input()
    performAddTableRow: () => void = null;
    @Input()
    performSetTableRows: (rows: TableRow[]) => void = null;
    @Input()
    mustRefresh: () => boolean = null;
    @Input()
    useTableMenu = SmTableComponent.defaultUseTableMenu;
    @Input()
    useColumnMenu = SmTableComponent.defaultUseColumnMenu;
    @Input()
    actionButtons = SmTableComponent.defaultActionButtons;
    _data: TableData;
    //eigentlich richtigerweise: colDefs: (ColDef<any> | ColGroupDef<any>)[];
    colDefs: ColDef[];
    context: OrdinaryObject = {};
    autoSizeStrategy: any = {
        type: "fitGridWidth"
    };
    internalPinnedBottomRows: any[] = [];
    ignoreDataChanges = false;

    components: { [p: string]: any } = {
        agColumnHeader: SmTableHeaderComponent
    };

    gridOptions: GridOptions = {
        getChildCount: data => (data as TableRow).canHaveChildren ? 1 : 0
    };

    getDataPath = (data: any): string[] => data.path;
    autoGroupColumnDef: ColDef;

    isEnterprise = !Utils.isNoe(Config.get().agGridEnterpriseKey);

    _selection: any;
    @Output()
    selectionChange = new EventEmitter<any>();


    @Output()
    onRowSelect = new EventEmitter<TableRow>();
    @Output()
    onRowRemoved = new EventEmitter<TableRowEvent>();
    @Output()
    onRowDoubleClicked = new EventEmitter<TableRow>();
    @Output()
    onCellClicked = new EventEmitter<TableCellEvent>();
    @Output()
    onCellValueChanged = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStarted = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStopped = new EventEmitter<TableCellEvent>();
    @Output()
    onCellFocused = new EventEmitter<TableCellEvent>();
    @Output()
    onFilterChanged = new EventEmitter<FilterChangedEvent>();
    @Output()
    onSortChanged = new EventEmitter<SortChangedEvent>();
    @Output()
    onReady = new EventEmitter<SmTableComponent>();

    @ViewChild("grid") grid: AgGridAngular;
    @ViewChild('grid', { read: ElementRef }) gridElement: ElementRef<HTMLElement>;

  
    fullRowEditForm: FrontendForm;
    fullRowEditRowNode: IRowNode;

    tabToNextCellBound = this.tabToNextCell.bind(this);

    prefersDark = window.matchMedia('(prefers-color-scheme: dark)');

    @Input()
    set checkBoxSelection(value: boolean) {
        this._checkBoxSelection = value;  
    }

    @Input()
    set pinnedBottomRows(value: TableRow[]) {
        this._pinnedBottomRows = value;
        this.updatePinnedBottomRows();
    }

    @Input()
    set flashChanges(value: boolean) {
        this._flashChanges = value;
        if (this.colDefs != null) {
            for (let [index, colDef] of this.colDefs.entries()) {
                colDef.enableCellChangeFlash = value && !this._data.columns[index].disableFlashOnChanges;
            }
        }
    }    

    @Input()
    set fullScreenRowEditor(value: boolean) {
        this._fullScreenRowEditor = value;
        if (this.colDefs != null) {
            let indexOfCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "editRowFullScreen");
            if (!this.isFullScreenRowEditorColumnVisible() && indexOfCol >= 0) {
                this.colDefs.splice(indexOfCol, 1);
            }            
            if (this.isFullScreenRowEditorColumnVisible() && indexOfCol < 0) {
                this.colDefs = [...this.colDefs, this.getFullScreenRowEditColDef()];
                this.updateColumnWidthsForMobile();
            }
        }
    }   


    resizeObserver: ResizeObserver;

    gridReady(_: GridReadyEvent): void {
        this.grid.api.setGridOption("suppressGroupChangesColumnVisibility", "suppressHideOnGroup");  
        
        this.resizeObserver = new ResizeObserver(() => {
            if (!this.horizontalScroll) {
                this.grid.api.sizeColumnsToFit();
            }
        });

        this.resizeObserver.observe(this.gridElement.nativeElement);

        this.onReady.emit(this);
    }

    updateColumnWidthsForMobile(): void {
        if (this.grid != null) {
            this.updateColumnWidthsForMobileInternal();
         }
         else {
            Utils.setTimerOnce(100, this.updateColumnWidthsForMobileInternal.bind(this));
         }
    }

    updateColumnWidthsForMobileInternal(): void {
        this.grid.api.setColumnsVisible(this._data.columns.slice(2).map(col => col.id), false);
    }

    sizeColumnsToFit(): boolean {
        if (this.grid?.api != null) {
            this.grid.api.sizeColumnsToFit();
            return true;
        }
        return false;
    }

    updateRemoveColumn(): void {
        if (this.colDefs != null) {
            let indexOfRemoveCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "remove");
            if ((!this._allowRemoveRow || this._readOnly) && indexOfRemoveCol >= 0) {
                this.colDefs.splice(indexOfRemoveCol, 1);
            }            
            if (this._allowRemoveRow && !this._readOnly && indexOfRemoveCol < 0) {
                this.colDefs.push(this.getRemoveColDef());
            }
        }
    }

    @Input()
    set allowAddRow(value: boolean) {
        this._allowAddRow = value;
        this.updatePinnedBottomRows();
    }

    @Input()
    set allowRemoveRow(value: boolean) {
        this._allowRemoveRow = value;
        this.updateRemoveColumn();        
    }

    @Input()
    set allowRowDrag(value: boolean) {
        this._allowRowDrag = value;
        if (this.colDefs != null) {
            let i = 0;
            for (let colDef of this.colDefs) {
                colDef.rowDrag = this._allowRowDrag && i++ == 0 ? params => (params.data as TableRow).canDrag : false;
            }
        }
    }

    @Input()
    set floatingFilter(value: boolean) {
        this._floatingFilter = value;
       
        if (this.colDefs != null) {
            for (let col of this.colDefs) {
                col.floatingFilter = this._floatingFilter;
            }

            this.grid?.api?.setGridOption("columnDefs", this.colDefs);
            }
    }

    @Input()
    set autoRowHeight(value: boolean) {
        this._autoRowHeight = value;
        if (this.colDefs != null) {
            for (let colDef of this.colDefs) {
                colDef.autoHeight = value;
            }
        }
    }

    @Input()
    set readOnly(value: boolean) {
        this._readOnly = value;
        this.updateRemoveColumn();
    }

    private getOneButtonColWidth(): Partial<ColDef> {
        let widthFlex = TableSizer.get(this.getTheme()).calculate(this._data, new TableColumn("", "", TableCellType.button, 1), false);
        return {
            width: widthFlex.width,
            flex: widthFlex.flex,
            minWidth: widthFlex.minWidth,
            maxWidth: widthFlex.maxWidth,
        };
    }

    private getRemoveColDef(): ColDef {
        return {
            colId: SmTableComponent.COLUMN_ID_REMOVE,
            context: {
                specialColumn: "remove",
                table: this,
                tableColumn: new TableColumn(SmTableComponent.COLUMN_ID_REMOVE, "", TableCellType.button, 1)
            },
            ...this.getOneButtonColWidth(),
            cellRenderer: SmTableCellComponent,
            cellRendererParams: {
                table: this
            },
            valueGetter: params => (params.data as TableRow).canDelete() ? [new TableCellButton("remove", "fas fa-trash", "Löschen")] : []
        };
    }

    private getFullScreenRowEditColDef(): ColDef {
        return {
            colId: SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN,
            context: {
                specialColumn: "editRowFullScreen",
                table: this,
                tableColumn: new TableColumn(SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN, "", TableCellType.button, 1)
            },
            ...this.getOneButtonColWidth(),
            cellRenderer: SmTableCellComponent,
            cellRendererParams: {
                table: this
            },
            pinned: "left",
            valueGetter: params => params.node.rowPinned != 'bottom' ? [new TableCellButton("editRow", "fas fa-pencil", "Zeile editieren")] : []
        };
    }

    sortCache = new Map<any, string>();

    @Input()
    set data(value: TableData) {
        if (this.ignoreDataChanges) {
            return;
        }
        let columnsChanged = !Arrays.equalsFlat(value?.columns, this._data?.columns);
        this._data = value;

        this.context = {
            table: this
        };
        if (this._data != null && columnsChanged) {
            this.colDefs = [];
            let colIdx = 0;
            let theme = this.getTheme();
            for (let col of this._data.columns) {
                let sortIndex = this._data.sort?.findIndex(sc => sc.id == col.id);
                let widthFlex = this.tableSizer ? col.getRealWidth(this._data, this.columnWidthsByContent, theme) : null;
                if (widthFlex != null && col.maxWidth != null && widthFlex.width > col.maxWidth) {
                    widthFlex.width = col.maxWidth;
                }
              //  console.log(col.id + " " + widthFlex?.width+ " " + widthFlex?.flex + " " + widthFlex?.minWidth + " " + widthFlex?.maxWidth);
                let colDef: ColDef = {
                    field: col.id,
                    colId: col.id,
                    cellDataType: col.cellType == TableCellType.bool ? "boolean" : col.cellType == TableCellType.int || col.cellType == TableCellType.decimal ? "number" : col.cellType == TableCellType.date || col.cellType == TableCellType.dateTime ? "date" : col.cellType == TableCellType.object ? "object" : "text",
                    valueGetter: params => {
                        if (params.node.group) {
                            return null;
                        }
                        if (params.data.values[col.id] == "_???addRow???_") {
                            return params.data.values[col.id];
                        }
                        let v = TableCell.getValue(params.data.values[col.id]);
                        return col.getValue(v, () => {
                            let colx = this.getColDefAndIndex(col.id);
                            return new TableCellEvent(v, params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index);
                        });
                    },
                    valueSetter: params => {
                        let colx = this.getColDefAndIndex(col.id);
                        col.setValue(new TableCellEvent(params.newValue, params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        return true;
                    },
                    valueFormatter: params => {
                        try {
                            let colx = this.getColDefAndIndex(col.id);

                            if (col.suppressRepeatingValues) {
                                for (let i = params.node.rowIndex- 1; i >= 0; i--) {
                                    let displayedRowAbove = this.grid.api.getDisplayedRowAtIndex(i);
                                    let row = displayedRowAbove?.data as TableRow;
                                    if (row?.virtual) {
                                        continue;
                                    }
                                    if (row?.values[col.id] == params.value) {
                                        return "";
                                    }
                                    break;
                                }
                            }

                            return params.value == "_???addRow???_" || colx == null ? params.value : 
                                col.formatValue(new TableCellEvent(params.value, params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index), this.app.language);
                        }
                        catch (error) {
                            console.log("Der Wert " + Objects.toString(params.value) + " für die Spalte " + col.id + " konnte nicht formatiert werden. " + error.message);
                            return "<???>";
                        }
                    },
                    context: {       
                        specialColumn: null,                 
                        table: this,
                        tableColumn: col
                    },
                    contextMenuItems: this.isEnterprise ? params => {
                        (params.event as any).preventDefault();
                        return [];
                    } : null,
                    suppressMovable: true,
                    editable: params => this.isCellEditableInternal(params.node, col),
                    cellEditorSelector: params => this.getCellEditor(params, col),
                    cellEditorPopup: col.editorPopup,
                    //singleClickEdit: true,
                    filter: col.isFilterable() ? col.cellType == TableCellType.string ? "agTextColumnFilter" : true : false,
                    filterParams: {
                        suppressExcelMode: true,
                        filterInputClass: "p-inputtext",
                        trimInput: true
                    },
                    floatingFilter: this._floatingFilter && col.isFilterable(),
                    headerName: col.caption,
                    //wrapHeaderText: true,
                    autoHeaderHeight: true,
                    pinned: col.frozen,
                    cellClass: params => {
                        if (params.node.group) {
                            return null;
                        }
                        let alignClass = col.align == "center" ? "ag-center-aligned-cell "  : col.align == "right" ? "ag-right-aligned-cell " : "";
                        if (params.data.values[col.id] instanceof TableCell) {
                            let result = (params.data.values[col.id] as TableCell).styleClass;
                            if (result != null) {
                                return alignClass + result;
                            }
                         }
                         if (col.getStyleClassCallback != null && params.node.rowPinned != 'bottom') {
                            let colx = this.getColDefAndIndex(col.id);
                            return alignClass + col.getStyleClassCallback(new TableCellEvent(TableCell.getValue(params.data.values[col.id]), params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        }
                        else {
                            return alignClass + col.styleClass;
                        }
                    },
                    cellStyle: params => {
                        if (params.node.group) {
                            return null;
                        }
                        if (params.data.values[col.id] instanceof TableCell) {
                            let result = params.data.values[col.id].style;
                            if (result != null) {
                                return result;
                            }
                        }
                        return null;
                    },
                    cellRendererSelector: params => this.getCellRenderer(params, col),
                    cellRenderer: col.cellRenderer,
                    enableCellChangeFlash: this._flashChanges && !col.disableFlashOnChanges,
                    rowDrag: this._allowRowDrag && colIdx == 0 ? params => (params.data as TableRow).canDrag : false, 
                    enableRowGroup: this.isEnterprise,
                    enableValue: this.isEnterprise,
                    enablePivot: this.isEnterprise,
                    showRowGroup: true,
                    sortable: col.isSortable(),
                    sort: sortIndex == -1 || sortIndex == null ? null : this._data.sort[sortIndex].ascending ? "asc" : "desc",
                    sortIndex: sortIndex != -1 ? sortIndex : null,
                    comparator: (col.useComparator == null ? col.cellType == TableCellType.string : col.useComparator) ? (a, b, cell1, cell2) => {
                        const getCmp = (v: any): string => {
                            (window as any).cmpCount = ((window as any).cmpCount ?? 0) + 1;
                            let cached = this.sortCache.get(v);
                            if (cached == null) {
                                (window as any).cmpCountCached = ((window as any).cmpCountCached ?? 0) + 1;
                                cached = Objects.toString(v).normalize('NFD').toLowerCase();
                                this.sortCache.set(v, cached);
                            }                          
                            return cached;  
                        };
                      
                        if (cell1.data instanceof TableRow && cell1.data.values[col.id] instanceof TableCell) {
                            a = (cell1.data.values[col.id] as TableCell).comparable ?? a;
                        }
                        if (cell2.data instanceof TableRow && cell2.data.values[col.id] instanceof TableCell) {
                            b = (cell2.data.values[col.id] as TableCell).comparable ?? b;
                        }

                        let cmp1 = col.cellType == TableCellType.string ? getCmp(a) : a;
                        let cmp2 = col.cellType == TableCellType.string ? getCmp(b) : b;

                        return cmp1 < cmp2 ? -1 : cmp1 > cmp2 ? 1 : 0;
                      } : null,
                    colSpan: params => this.getCellColSpan(params, col),
                    tooltipValueGetter: params => {
                        if (col.toolTipGetter != null && params.node.rowPinned != 'bottom') {
                            let colx = this.getColDefAndIndex(col.id);
                            return colx == null ? null : col.toolTipGetter(new TableCellEvent(TableCell.getValue(params.data.values[col.id]), params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        }
                         else {
                            return null;
                         }
                    },
                    initialWidth: widthFlex?.width,
                    initialFlex: widthFlex?.flex,    
                    minWidth: widthFlex?.minWidth,
                    maxWidth: widthFlex?.maxWidth,                
                    resizable: col.resizable,
                    autoHeight: this._autoRowHeight
                };
                this.colDefs.push(colDef);
                colIdx++;
            }
            if (this._allowRemoveRow && !this._readOnly) {
                this.colDefs.push(this.getRemoveColDef());
            }
            if (this.isFullScreenRowEditorColumnVisible() && this.app.isMobile()) {
                this.colDefs.push(this.getFullScreenRowEditColDef());
            }

            let delCols: number[] = [];
            let groupCaptions = Arrays.getUnique(this._data.columns.filter(col => !Utils.isNoe(col.groupCaption)).map(col => col.groupCaption));
            for (let groupCaption of groupCaptions) {
                let groupColumn = null;
                colIdx = 0;
                for (let col of this._data.columns) {
                    if (col.groupCaption == groupCaption) {
                        if (groupColumn == null) {
                                groupColumn = {
                                headerName: groupCaption,
                                children: [ 
                                    this.colDefs[colIdx] 
                                ]
                            };
                            this.colDefs[colIdx] = groupColumn;
                        }
                        else {
                            groupColumn.children.push(this.colDefs[colIdx]);
                            delCols.push(colIdx);
                        }
                    }
                    colIdx++;
                }
            }

            for (let delCol of Arrays.reverse(Arrays.sort(delCols))) {
                this.colDefs.splice(delCol, 1);
            }

            if (this.isTree) {
            /*    this.autoGroupColumnDef = this.colDefs.splice(0, 1)[0];
                if (this.autoGroupColumnDef != null) {
                    this.autoGroupColumnDef.field = "values.key";
                }*/
                //PRAGMATIC Hardcodiert
               /* this.autoGroupColumnDef = {
                    headerName: "Eigenschaft",
                    field: "values.key",
                    cellRendererParams: {
                        suppressCount: true
                    },
                    contextMenuItems: this.isEnterprise ? params => {
                        (params.event as any).preventDefault();
                        return [];
                    } : null
                };*/
            }
            else {
                this.autoGroupColumnDef = null;
            }
            
            this.updatePinnedBottomRows();

            if (this.isFullScreenRowEditorColumnVisible()) {
                this.updateColumnWidthsForMobile();
            }
            if (!this.horizontalScroll) {
                GuiUtils.angularTimer(() => {
                    this.doSizeColumnsToFit = true;
                });
            }
        }
    }

    private updatePinnedBottomRows(): void {
        this.internalPinnedBottomRows = [...!this._readOnly && this._allowAddRow && this.colDefs != null ? 
            [new TableRow("", {}, Arrays.toOrdinaryObject(this.colDefs, col => col.colId, col => col.colId == this.colDefs[0].colId ? "_???addRow???_" : ""))] : [],
            ...(this._pinnedBottomRows ?? [])];
    }

/*
    private string getCellStyleClass(node: IRowNode, column: TableColumn): string {
        if (node.data.values[column.id] instanceof TableCell && node.data.values[col.id].styleClass) {
            
        }
        } ? params.data.values[col.id].styleClass : col.getStyleClassCallback != null && params.node.rowPinned != 'bottom' ? 
                        col.getStyleClassCallback(TableCell.getValue(params.data.values[col.id]), params.node.rowIndex, this.getColIndex(col.id)) : null
    }

  */  
 
    private isCellEditableInternal(rowNode: IRowNode, column: TableColumn): boolean {
        if (this._readOnly || rowNode.rowPinned == "bottom") {
            return false;
        }
        let col = this.getColDefAndIndex(column.id);
        return column.isEditable(new TableCellEvent(TableCell.getValue(rowNode.data.values[column.id]), rowNode.data as TableRow, col.def, rowNode.rowIndex, col.index));
    }

    private getCellColSpan(params: ColSpanParams, col: TableColumn): number {
        if (params.data == null) {
            return 1;
        }
        let v = params.data.values[col.id];
        if (v == "_???addRow???_") {
            return this._data.columns.length + (this._allowRemoveRow ? 1 : 0);
        }
        if (v instanceof TableCell) {
            return  v.colSpan == -1 ? this._data.columns.length + (this._allowRemoveRow ? 1 : 0) - this.getColIndex(col.id) : v.colSpan;
        }
        return 1;
    }

    private getCellRenderer(params: ICellRendererParams, column: TableColumn): CellRendererSelectorResult {
        if  (params.node.group) {
            return null;
        }
        if (params.colDef.cellDataType == "boolean" && params.value == null) {
            return { component: () => "" };
        }
        if (this.overrideUseTableCellRenderer === true) {
            return { component: SmTableCellComponent };
        }
        if (this.overrideUseTableCellRenderer === false) {
            return null;
        }
        if (params.data.values[column.id] == "_???addRow???_" && column.id == this.colDefs[0].colId) {
            // Plus-Button in der Add-Zeile
            return { component: SmTableCellComponent };
        }
        if (column.cellType == TableCellType.button || column.cellType == TableCellType.color || column.cellType == TableCellType.link) {
            return { component: SmTableCellComponent };
        }
        if ((params.node as any).hover && column.cellType != TableCellType.bool) {
            //Mouse-Hover
            return { component: SmTableCellComponent };
        }
        if (params.data.values[column.id] instanceof TableCell && params.data.values[column.id].keepHtml) {
            //HTML beibehalten
            return { component: SmTableCellComponent };
        }
        if (column.keepHtml) {
            return { component: p => p.value };
        }
        return null;
    }
    
    private getCellEditor(params: ICellEditorParams, column: TableColumn): CellEditorSelectorResult {
        if (!column.editable || params.node.rowPinned == "bottom") {
            return undefined;
        }
        if (column.editor != null) {
            let editor = column.getEditor(() => new TableCellEvent(TableCell.getValue(params.data.values[column.id]), params.data as TableRow, this.colDefs.find(colDef => colDef.colId == column.id), params.node.rowIndex, this.getColIndex(column.id)));
            if (editor == null) {
                return undefined;
            }
            return {
                component: SmTableCellEditorComponent,
                params: {
                    editor                       
                }
            };
        }        
        if (column.cellType == TableCellType.string) {
            return {
                component: "agTextCellEditor", 
                params: {
                    maxLength: column.editableMaxLength
                }
            };
        }
        if (column.cellType == TableCellType.int || column.cellType == TableCellType.decimal) {
            return {
                component: "agNumberCellEditor", 
                params: {
                    precision: column.numDecimalDigits,
                    max: column.editableMaxLength != null ? 10 ** column.editableMaxLength - 1 : null
                }
            };
        }
        if (column.cellType == TableCellType.date) {
            return {
                component: "agDateCellEditor", 
                params: {
                }
            };
        }
        return null;
    }

    @Input()
    set selection(value: any) {
        this._selection = value;
    }

    doSizeColumnsToFit = false;
    
    selectFirstRow(): void {
        this.grid.api.getDisplayedRowAtIndex(0)?.setSelected(true);
    }

    ngDoCheck(_): void {
        if (this.grid != null && this.mustRefresh?.()) {
            this.refresh();
        }
            
        if (this.fullRowEditForm != null) {
            for (let field of this.fullRowEditForm.fields) {
                this.columnToField(field, null);
            }
        }
        this.grid?.api?.setGridOption("headerHeight", this.headerHeight);

        if (this.doSizeColumnsToFit) {
            if (this.horizontalScroll) {
                this.doSizeColumnsToFit = false;
            }
            else {
                if (this.app.advancedDeveloper) {
                    console.log("advancedDeveloper: sizeColumnsToFit " + Arrays.itemsToString(this._data?.columns, ", ", c => c.caption));
                }
                this.doSizeColumnsToFit = !this.sizeColumnsToFit();
            }
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    refresh(): void {
        this.grid.api.refreshCells({ force: true});
    }

    isFullScreenRowEditorColumnVisible(): boolean {
        return this._fullScreenRowEditor && this.app.isMobile();
    }

    async editRowFullScreen(rowNode: IRowNode): Promise<void> {
        this.fullRowEditForm = new FrontendForm(this.colDefs
            .filter(colDef => colDef.colId != SmTableComponent.COLUMN_ID_EDIT_ROW_FULL_SCREEN && colDef.colId != SmTableComponent.COLUMN_ID_REMOVE)
            .map(colDef => this.columnToField(null, colDef)));         
        this.fullRowEditRowNode = rowNode;

        try {
            await this.app.dialog.form(this.fullRowEditForm, "Zeile editieren", this.app.dialog.ok, this.app.isMobile());
        }
        finally {
            this.fullRowEditForm = null;
            this.fullRowEditRowNode = null;
        }
    }

    private getTableColumn(id: string): TableColumn {
        return this._data.columns.find(col2 => col2.id == id);
    }

    private columnToField(field: FrontendField, colDef: ColDef): FrontendField {
        if (colDef == null) {
            colDef = this.colDefs.find(colDef2 => colDef2.colId == field.id);
        }
        let col = this.getTableColumn(colDef.colId);
        if (field == null) {
            field = new FrontendField(colDef.colId, colDef.headerName, TableCellType.toFrontendFieldType((colDef.context.tableColumn as TableColumn).cellType), {
                onValueChanged: () => {
                    const params = {
                        data: this.fullRowEditRowNode.data,
                        newValue: field.value,
                        oldValue: this.fullRowEditRowNode.data[colDef.colId],
                        colDef,
                      //  column: column,
                        node: this.fullRowEditRowNode,
                        api: this.grid.api,
                      //  columnApi: this.gridColumnApi,
                        context: this.grid.context
                      };
                    (colDef.valueSetter as any)(params);
                    
                    this.cellValueChanged({ data: this.fullRowEditRowNode.data, colDef, node: this.fullRowEditRowNode });
                }
            });
            field.maxValue = col.cellType == TableCellType.int && col.editableMaxLength != null ? 10 ** col.editableMaxLength - 1 : null;
        }

        if (this.fullRowEditRowNode != null) {
            if (field.value == null) {
                field.value = this.grid.api.getCellValue({ rowNode: this.fullRowEditRowNode, colKey: colDef.colId});
            }
            field.enabled = this.isCellEditableInternal(this.fullRowEditRowNode, colDef.context.tableColumn as TableColumn);
            let editor = col.getEditor(() => new TableCellEvent(null, this.fullRowEditRowNode?.data as TableRow, colDef, this.fullRowEditRowNode?.rowIndex ?? -1, this.getColIndex(colDef.colId)));
            let isDropdown = editor != null && editor.type == FrontendFieldType.dropdown;
            field.listItems = isDropdown ? editor.listItems : null;
            field.type = isDropdown ? FrontendFieldType.dropdown : field.type;
        }
        return field;
    }

    isCellEditable(rowIndex: number, col: Column): boolean {
        const colDef = col.getColDef();
      
        if (colDef.editable === true) {
          return true;
        }
      
        if (typeof colDef.editable === 'function') {
          const rowNode = this.grid.api.getDisplayedRowAtIndex(rowIndex);
          if (!rowNode) {
            return false;
          }
      
          const params = {
            node: rowNode,
            data: rowNode.data,
            rowIndex,
            colDef,
            column: col,
            api: this.grid.api,
            context: this.context
          };
      
          return colDef.editable(params);
        }
      
        return false;
    }

    getFirstEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();
    
        for (let col of columns) {
            const colDef = col.getColDef();
    
            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);
    
            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }
    
        return null;
    }

    getLastEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();
    
        for (let i = columns.length - 1; i >= 0; i--) {
            const col = columns[i];
            const colDef = col.getColDef();
    
            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);
    
            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }
    
        return null;
    }

    isCompact(): boolean {
        return this.compact ?? this.app.compactGui;
    }

    getTheme(): string {
        return (this.isCompact() ? "balham" : "quartz") + (this.app.isDarkMode() ? "-dark" : "");
    }

    getRowHeight(event: RowHeightParams): number {
        return (event.data as TableRow).height;
    }

    cellKeyDown(event: any): void {
        if (this.addRowOnTabInLastCell && event.event.key === 'Tab') {
            const lastRowIndex = this.grid.api.getDisplayedRowCount() - 1;
            
            if (event.rowIndex === lastRowIndex && event.column.colId == this.getLastEditableColId(event.rowIndex as number)) {
                event. event.preventDefault(); 
                this.addRow();
                //Angular
                GuiUtils.angularTimerDefer2(() => {
                    this.grid.api.startEditingCell({
                    rowIndex: lastRowIndex + 1,
                    colKey: this.getFirstEditableColId(lastRowIndex)
                    });
                });
            }
        }
    }

      /*  let params = paramsx as any as CellKeyDownEvent;
        if ((params.9event as any as KeyboardEvent).key === 'ArrowUp') {
            this.grid.api.stopEditing(); 
      
            const nextRowIndex = params.node.rowIndex - 1;
            if (nextRowIndex >= 0) {
              params.api.setFocusedCell(nextRowIndex, params.column.getColId());
              params.api.startEditingCell({
                rowIndex: nextRowIndex,
                colKey: params.column.getColId(),
              });
            }
        }
        else if ((params.event as any as KeyboardEvent).key === 'ArrowDown') {
            this.grid.api.stopEditing(); 
        
            const nextRowIndex = params.node.rowIndex + 1;
            if (nextRowIndex < params.api.getDisplayedRowCount()) {
                params.api.setFocusedCell(nextRowIndex, params.column.getColId());
                params.api.startEditingCell({
                rowIndex: nextRowIndex,
                colKey: params.column.getColId(),
                });
            }
        }*/
    
    cellMouseOver(event: any): void {
        event.node.hover = true;
        event.api.refreshCells({ rowNodes: [event.node], force: true, suppressFlash: true });
    }


    cellMouseOut(event: any): void {
        event.node.hover = false;
        event.api.refreshCells({ rowNodes: [event.node], force: true, suppressFlash: true });
    }

    cellClicked(event: CellClickedEvent): void {
        let cell = Objects.castOrNull(event.data.values[event.colDef.colId], TableCell);
        let toolTipComponent = cell?.toolTip?.getComponentOnClick();
        if (toolTipComponent != null) {
            //TODO
        }

        if (this.editOnClick && this.grid.api.getCellEditorInstances({ rowNodes: [event.node], columns: [event.column]}).length == 0) {
            this.grid.api.startEditingCell({
                rowIndex: event.rowIndex,
                colKey: event.column.getId()
            });
        }
    }

    cellEditingStarted(event: CellEditingStartedEvent): void {
        if (this.onCellEditingStarted.observed) {
            let row = event.data as TableRow;
            this.onCellEditingStarted.emit(new TableCellEvent(TableCell.getValue(row.values[event.colDef.colId]), row, event.colDef, event.node.rowIndex, this.getColIndex(event.colDef.colId)));         
        }
    }

    cellEditingStopped(event: CellEditingStoppedEvent): void {
        if (this.onCellEditingStopped.observed) {
            let row = event.data as TableRow;
            //CheckboxEditor hat hier eine Besonderheit. Wenn man vor dem Editieren in die Zelle klickt, damit der Editor startet, klappt alles,
            //aber wenn man ohne dies zu tun direkt die Checkbox anklickt, steht in event.newValue nichts drinnen, deshalb dieser Workaround
            let col = this.getTableColumn(event.colDef.colId);
            let value = event.newValue;
            if (col.cellType == TableCellType.bool && value == null) {
                value = row.values[col.id];
            }
            this.onCellEditingStopped.emit(new TableCellEvent(value, row, event.colDef, event.node.rowIndex, this.getColIndex(event.colDef.colId)));         
        }
    }

    cellContextMenu(event: CellContextMenuEvent): void {
        if (this.ignoreCellContextMenu) {
            return;
        }

        event.event.stopPropagation();
        event.event.preventDefault();

        let row = event.data as TableRow;
        let col = event.colDef ;
        let column = this.getTableColumn(col.field == "values.outline" ? "outline" : col.colId);
        if (column == null) {
            return;
        }
        let callback = column.getContextMenuItems ?? row.getContextMenuItems;
        let menuItems = callback != null ? callback(new TableCellEvent(event.value, row, col, event.node.rowIndex, this.getColIndex(col.colId))) : [];
        if (!Utils.isNoe(menuItems)) {
            this.app.openContextMenu(menuItems);
        }
    }

    ignoreCellContextMenu = false;

    gridContextMenu(event: PointerEvent): void {     
        if (event.getModifierState("Control")) {
            this.ignoreCellContextMenu = true;
            Utils.setTimerOnce(100, () => {
                this.ignoreCellContextMenu = false;
            });
            return;
        }  
        event.stopPropagation();
        event.preventDefault();
    }

    cellFocused(_: CellFocusedEvent): void {
        if (this.onCellFocused.observed) {
       /*     let row = event.data as TableRow;
            let col = event.colDef as ColDef;
            this.onCellFocused.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.rowIndex, this.getColIndex(col.colId)));         
       */ }
    }

    getRowClass(params: any): string {
        return (params.data as TableRow)?.styleClass;
    }

    rowDoubleClicked(event: any): void {
        this.onRowDoubleClicked.emit(event.data as TableRow);
    }

    rowSelected(event: any): void {
        if (event.node.isSelected()) {
            this.onRowSelect.emit(event.data as TableRow);
        }
    }

    rowDragEnd(event: RowDragEndEvent): void {
        if (this.rowDragManaged) {
            const newOrder: TableRow[] = [];
            this.grid.api.forEachNode((node) => {
            newOrder.push(node.data as TableRow); 
            });
            this._data.rows = newOrder; 
            if (this.performSetTableRows != null) {
                this.performSetTableRows(newOrder);
            }
        }
        else {
            if (event.overNode == null || this.dropDraggedRow == null) {
                return;
            }
            this.dropDraggedRow(event.node.data as TableRow, event.overNode.data as TableRow, false);
        }        
    }

    rowDragMove(event: RowDragMoveEvent): void {
        if (event.overNode == null) {
            return;
        }
        let result = this.dropDraggedRow != null ? this.dropDraggedRow(event.node.data as TableRow, event.overNode.data as TableRow, true) : null;
        if (result?.dropText != null) {
            const elHint = document.querySelector('.ag-dnd-ghost');
            const elLabel = document.querySelector('.ag-dnd-ghost-label') as HTMLElement;
            if (elHint == null || elLabel == null) {
                return;
            }
        
            elLabel.innerText = result.dropText;
            for (let clazz of Array.from(elHint.classList)) {
                if (!clazz.startsWith("ag-")) {
                    elHint.classList.remove(clazz);
                }
            }
            for (let clazz of result.styleClasses ?? []) {
                elHint.classList.add(clazz);
            }
        }
    }

    public getRowId: GetRowIdFunc = (params: GetRowIdParams) => (params.data as TableRow).id;

    selectionChanged(event: any): void {
        this._selection = event.api.getSelectedRows().map(r => r as TableRow);
        this.selectionChange.emit(this._selection);
    }

    
    setSelectedRowIndex(index: number): void {
        this.grid.api.getDisplayedRowAtIndex(index)?.setSelected(true);
    }

    getColIndex(id: string): number {
        return this.colDefs.findIndex(colDef => colDef.colId == id);
    }

    getColDefAndIndex(id: string): { def: ColDef; index: number } {
        let i = 0;
        for (let def of this.colDefs) {
            if (def.colId == id) {
                return { def, index: i };
            }
            for (let def2 of (def as ColGroupDef).children ?? []) {
                if ((def2 as ColDef).colId == id) {
                    return { def: (def2 as ColDef), index: i };
                }
                i++;
            }
            i++;
        }
        return null;
    }

    cellValueChanged(event: Partial<CellValueChangedEvent>): void {
        let row = event.data as TableRow;
        let col = event.colDef;
        this.onCellValueChanged.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.node.rowIndex, this.getColIndex(col.colId)));     
    }

    sortChanged(event: SortChangedEvent): void {
        this.sortCache.clear();
        this.onSortChanged?.emit(event);
    }

    filterChanged(event: FilterChangedEvent): void {
        this.onFilterChanged?.emit(event);
    }

    getUnfilteredRows(): TableRow[] {
        let result: TableRow[] = [];
        this.grid.api.forEachNodeAfterFilterAndSort((node) => {
            result.push(node.data as TableRow);
        });

        return result;
    }

    exportCsv(): void {
        GuiUtils.downloadBytesAsFile(GuiUtils.stringAsciiToArrayBuffer(this._data.toCsv(this.getUnfilteredRows())), "Tabelle.csv");
    }

    async exportXlsx(): Promise<void> {
        let csv = this._data.toCsv(this.getUnfilteredRows());
        let columnState = this.grid.api.getColumnState();
        let xlsx = await GuiUtils.convertCsvToExcel(csv, this._data.columns.map(col => Objects.fromPlain(ExcelColumn, {
            dataType: TableCellType.toGenericDataType(col.cellType),
            width: columnState.find(cs => cs.colId == col.id)?.width * 0.15
        })));
        GuiUtils.downloadBytesAsFile(GuiUtils.base64ToArrayBuffer(xlsx), "Tabelle.xlsx");
    }

    async asciiTableToClipboard(): Promise<void> {
        await GuiUtils.copyToClipboard(this._data.toAsciiTable(this.getUnfilteredRows()));
    }

    toggleFilterRow(): void {
        if (!this.allowFilter) {
            return;
        }
        this.floatingFilter = !this._floatingFilter;
    }

    setFilter(columnId: string, value: string | OrdinaryObject): void {
        if (!this.allowFilter) {
            return;
        }
        this.grid.api.setFilterModel({
            [columnId]: Strings.is(value) ? {
                filterType: "text",
                type: "contains",
                filter: value
            } : value as OrdinaryObject
        });
        this.grid.api.onFilterChanged();
    }

    openSettings(event: any): void {
        let unfilteredRows = this.getUnfilteredRows();
        this.app.openContextMenu([
            this.isEnterprise ? {
                label: this.displaySideBar ? "Sidebar verstecken" : "Sidebar anzeigen",
                command: () => {
                    this.displaySideBar = !this.displaySideBar;
                }
            } : null,
            this.allowFilter ? {
                label: this.app.t("table.toggleFilterRow"),
                command: this.toggleFilterRow.bind(this)
            } : null,
            {
                label: this.app.t("table.exportCsv"),
                command: this.exportCsv.bind(this)
            },
            GuiUtilsInternal.convertCsvToExcelFunction != null ? {
                label: this.app.t("table.exportXlsx"),
                command: this.exportXlsx.bind(this)
            } : null,
            {
                label: this.app.t("table.asciiTableToClipboard"),
                command: this.asciiTableToClipboard.bind(this)
            },
            this.app.advancedDeveloper ? {
                label: "Debug: TableCellRenderer nutzen",
                badge: this.overrideUseTableCellRenderer == null ? "DYNAMISCH" : this.overrideUseTableCellRenderer ? "AN" : "AUS",
                command: () => {
                    this.overrideUseTableCellRenderer = Utils.cycleTriState(this.overrideUseTableCellRenderer);
                }
            } : null,
            this.app.advancedDeveloper ? {
                label: "Debug: Datenänderungen ignorieren",
                badge: this.ignoreDataChanges ? "AN" : "AUS",
                command: () => {
                    this.ignoreDataChanges = !this.ignoreDataChanges;
                }
            } : null,
            this.app.advancedDeveloper ? {
                label: "Kompakter Modus",
                badge: this.isCompact() ? "AN" : "AUS",
                command: () => {
                    this.compact = !this.isCompact();
                }
            } : null,
            this.app.advancedDeveloper ? {
                label: "Spaltenbreiten neu berechnen",
                command: () => {
                    this.sizeColumnsToFit()
                }
            } : null,
            {
                separator: true
            },
            {
                label: "Anzahl Zeilen: " + this._data.rows.length,
                disabled: true
            },
            unfilteredRows.length != this._data.rows.length ? {
                label: "Anzahl sichtbarer Zeilen: " + unfilteredRows.length,
                disabled: true
            } : null           
        ]);
        event.stopPropagation();
    }

    getSelectedRowCount(): number {
        return this.grid?.api?.getSelectedNodes()?.length ?? 0;
    }

    makeRowVisible(rowId: string): void {
        const rowNode = this.grid.api.getRowNode(rowId);

        if (rowNode == null) {
            return; 
        }

        this.grid.api.ensureNodeVisible(rowNode, 'middle');
    }

    addRow(): void {
        if (this.performAddTableRow != null) {
            this.performAddTableRow();
        }
        GuiUtils.angularTimer(() => this.grid.api.ensureIndexVisible(this._data.rows.length - 1, 'bottom'));
    }

    async removeRow(row: TableRow, showWarningMessage = false): Promise<void> {
        if (showWarningMessage && this.removeRowWarningMessage != null && !await this.app.dialog.yesNo(this.removeRowWarningMessage, this.app.t("general.warning"))) {
            return;
        }
        let index = this._data.rows.findIndex(row2 => row2 == row);
        if (index >= 0) {
            if (this.handleRemove) {
                this._data.rows = Arrays.withoutIndex(this._data.rows, index);
            }
            this.onRowRemoved.emit(new TableRowEvent(row, index));
        }
    }

    tabToNextCell(params: any): boolean | CellPosition {
        const nextRowIndex =  params.previousCellPosition.rowIndex + (params.backwards ? -1 : 1);

        if (nextRowIndex < 0 || nextRowIndex >= this.grid.api.getDisplayedRowCount()) {
            return null;
        }
    
        return {
          rowIndex: nextRowIndex,
          column: params.previousCellPosition.column,
          rowPinned: params.previousCellPosition.rowPinned
        };
    }

    stopCellEditing(): void {
        this.grid.api.stopEditing();
    }

    getGroupColumnIds(): string[] {
        return this.isEnterprise ? this.grid.api.getRowGroupColumns().map(c => c.getColId()) : [];
    }
    
    addGroupColumn(colId: string): void {
        const currentGroups = this.getGroupColumnIds();

        if (!currentGroups.includes(colId)) {
            const newGroups = [...currentGroups, colId];
            this.grid.api.setRowGroupColumns(newGroups);
        }

        this.grid.api.refreshClientSideRowModel('group');
    }

    removeGroupColumn(colId: string): void {
        const currentGroups = this.getGroupColumnIds();
        const newGroups = currentGroups.filter(id => id !== colId);

        this.grid.api.setRowGroupColumns(newGroups);
        this.grid.api.refreshClientSideRowModel('group');
    }

    setQuickFilterText(text: string): void {
        this.grid.api.setGridOption('quickFilterText', text);
        this.grid.api?.refreshCells({ force: true });
    }

    getTableStatus(): SmTableStatus {
        return Objects.fromPlain(SmTableStatus, {
            columnStatus: this.grid.api.getColumnState().map(col => Objects.fromPlain(SmTableColumnStatus, {
                columnId: col.colId,
                filter: this.grid.api.getFilterModel()[col.colId],
                sort: col.sort,
                sortIndex: col.sortIndex
            }))
        });
    }

    applyTableStatus(status: SmTableStatus): void {
        const currentState = this.grid.api.getColumnState();

        const statusByColId = new Map(status.columnStatus.map(cs => [cs.columnId, cs]));

        const mergedState: ColumnState[] = currentState.map(col => {
            const saved = statusByColId.get(col.colId);
            if (!saved) {
                return {
                    ...col,
                    sort: null,
                    sortIndex: null
                };
            }

            return {
                ...col,
                sort: saved.sort ?? null,
                sortIndex: saved.sortIndex ?? null
            };
        });

        this.grid.api.applyColumnState({
            state: mergedState,
            applyOrder: false
        });

        const filterModel: FilterModel = {};

        for (const cs of status.columnStatus) {
            if (cs.filter != null) {
                filterModel[cs.columnId] = cs.filter;
            }
        }

        this.grid.api.setFilterModel(filterModel);

        this.grid.api.onFilterChanged();
    }

}